import { useState } from 'react'
import { useQuery } from 'react-query'
import { 
  Search, 
  Activity, 
  AlertCircle,
  TrendingUp,
  Filter,
  RefreshCw,
  BarChart3,
  MousePointer2
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  SimpleGrid,
  Box,
  Select,
  useToast,
  Progress,
  Skeleton,
  FormControl,
  FormLabel,
  Badge,
  Tooltip as ChakraTooltip,
} from '@chakra-ui/react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  FunnelChart,
  Funnel,
  LabelList,
  Cell
} from 'recharts'
import { apiClient as api } from '../services/api'

// --- Components ---

function SearchPopularityChart({ data }: { data?: any[] }) {
  return (
    <Card variant="glass" shadow="sm">
      <CardHeader pb={0}>
        <HStack spacing={3}>
          <Box p={2} bg="cyan.100" borderRadius="lg">
            <Search size={20} color="var(--chakra-colors-cyan-600)" />
          </Box>
          <VStack align="start" spacing={0}>
            <Heading size="md" color="gray.900">Route Demand (Search Frequency)</Heading>
            <Text fontSize="sm" color="gray.500">Most searched routes by users</Text>
          </VStack>
        </HStack>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data || []} layout="vertical" margin={{ left: 20, right: 30, top: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
            <XAxis type="number" stroke="#64748B" />
            <YAxis dataKey="routeName" type="category" width={150} stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Bar dataKey="searchCount" fill="#0EA5E9" radius={[0, 4, 4, 0]} name="Search Count" barSize={30}>
               {data?.map((_, index) => (
                <Cell key={`cell-${index}`} fill={index === 0 ? '#0284C7' : '#0EA5E9'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

function BookingFunnelChart({ data }: { data?: any }) {
  // Map internal stats to funnel stages
  const stats = data?.statsByType || {};
  const funnelData = [
    { value: data?.totalAttempts || 0, name: 'Search Result Hits', fill: '#94A3B8' },
    { value: stats['created'] || 0, name: 'Booking Initiated', fill: '#3B82F6' },
    { value: stats['payment_initiated'] || 0, name: 'Payment Attempted', fill: '#F59E0B' },
    { value: stats['confirmed'] || 0, name: 'Conversion (Paid)', fill: '#22C55E' },
  ].filter(d => d.value > 0);

  return (
    <Card variant="glass" shadow="sm">
      <CardHeader pb={0}>
        <HStack spacing={3}>
          <Box p={2} bg="blue.100" borderRadius="lg">
            <TrendingUp size={20} color="var(--chakra-colors-blue-600)" />
          </Box>
          <VStack align="start" spacing={0}>
            <Heading size="md" color="gray.900">Conversion Funnel</Heading>
            <Text fontSize="sm" color="gray.500">From intent to successful booking</Text>
          </VStack>
        </HStack>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={400}>
          <FunnelChart>
            <Tooltip 
              contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
            />
            <Funnel
              dataKey="value"
              data={funnelData}
              isAnimationActive
            >
              <LabelList position="right" fill="#475569" stroke="none" dataKey="name" fontSize={12} />
              <LabelList position="center" fill="#fff" stroke="none" dataKey="value" fontSize={14} fontWeight="bold" />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}

function FailureAnalytics({ data }: { data?: any }) {
  return (
    <Card variant="glass" shadow="sm">
      <CardHeader>
        <HStack spacing={3}>
          <Box p={2} bg="red.100" borderRadius="lg">
            <AlertCircle size={20} color="var(--chakra-colors-red-600)" />
          </Box>
          <VStack align="start" spacing={0}>
            <Heading size="md" color="gray.900">Failure Diagnostics</Heading>
            <Text fontSize="sm" color="gray.500">Why are users failing to book?</Text>
          </VStack>
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={6} align="stretch">
           <SimpleGrid columns={3} spacing={4}>
            <Box p={4} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.100">
              <Text fontSize="xs" color="gray.500" mb={1} fontWeight="bold">TOTAL ATTEMPTS</Text>
              <Text fontSize="2xl" fontWeight="bold">{data?.totalAttempts || 0}</Text>
            </Box>
            <Box p={4} bg="red.50" borderRadius="xl" border="1px solid" borderColor="red.100">
              <Text fontSize="xs" color="red.500" mb={1} fontWeight="bold">FAILURES</Text>
              <Text fontSize="2xl" fontWeight="bold" color="red.600">{data?.failureCount || 0}</Text>
            </Box>
            <Box p={4} bg="green.50" borderRadius="xl" border="1px solid" borderColor="green.100">
              <Text fontSize="xs" color="green.600" mb={1} fontWeight="bold">ERROR RATE</Text>
              <Text fontSize="2xl" fontWeight="bold" color="green.700">
                {data?.totalAttempts > 0 ? ((data.failureCount / data.totalAttempts) * 100).toFixed(1) : 0}%
              </Text>
            </Box>
          </SimpleGrid>

          <Box>
            <Text fontWeight="bold" mb={4} fontSize="sm" color="gray.700">TOP ERROR MESSAGES</Text>
            <VStack spacing={4} align="stretch">
              {(data?.failureReasons || []).map((fail: any, idx: number) => (
                <Box key={idx} p={3} bg={idx === 0 ? "red.50" : "transparent"} borderRadius="lg" border={idx === 0 ? "1px solid" : "none"} borderColor="red.100">
                  <Flex justify="space-between" align="center" mb={2}>
                    <Badge colorScheme="red" variant="subtle" fontSize="xs" px={2} py={0.5} borderRadius="md">
                      {fail.count} occurrences
                    </Badge>
                    <Text fontSize="xs" color="gray.500">{((fail.count / data.failureCount) * 100).toFixed(1)}% of errors</Text>
                  </Flex>
                  <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={2}>{fail.reason}</Text>
                  <Progress 
                    value={(fail.count / data.failureCount) * 100} 
                    size="xs" 
                    colorScheme="red" 
                    borderRadius="full"
                    bg="gray.100"
                  />
                </Box>
              ))}
              {(!data?.failureReasons || data.failureReasons.length === 0) && (
                <Flex direction="column" align="center" py={10}>
                  <Text fontSize="sm" color="gray.400" fontStyle="italic">No detailed error logs found for this period</Text>
                </Flex>
              )}
            </VStack>
          </Box>
        </VStack>
      </CardBody>
    </Card>
  )
}

export default function InsightsPage() {
  const [period, setPeriod] = useState('30d')
  const toast = useToast()

  const { data: insights, isLoading, refetch, isFetching } = useQuery(
    ['user-insights', period],
    async () => {
      const [searchesRes, attemptsRes] = await Promise.all([
        api.get(`/api/cms/dashboard/searches?period=${period}`),
        api.get(`/api/cms/dashboard/booking-attempts?period=${period}`)
      ])
      return {
        searches: searchesRes.data.data,
        attempts: attemptsRes.data.data
      }
    },
    {
      staleTime: 60000,
      onError: () => {
        toast({
          title: 'Error loading insights',
          description: 'Could not fetch behavior analytics',
          status: 'error',
          duration: 5000,
        })
      }
    }
  )

  const handleRefresh = async () => {
    await refetch()
    toast({
      title: 'Insights Updated',
      status: 'success',
      duration: 2000,
    })
  }

  return (
    <VStack spacing={8} align="stretch">
      <Flex justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          <Heading size="lg" fontWeight="bold">User Insights</Heading>
          <Text color="gray.500">Analyze user demand, search intent, and booking friction</Text>
        </VStack>
        <HStack spacing={4}>
          <FormControl display="flex" alignItems="center">
            <FormLabel mb="0" fontSize="sm" mr={2}>Period:</FormLabel>
            <Select 
              size="sm" 
              w="150px" 
              borderRadius="lg" 
              value={period} 
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="3m">Last 3 Months</option>
            </Select>
          </FormControl>
          <Button 
            leftIcon={<RefreshCw size={18} className={isFetching ? "animate-spin" : ""} />} 
            variant="outline" 
            size="sm" 
            borderRadius="lg"
            onClick={handleRefresh}
            isLoading={isFetching}
          >
            Refresh
          </Button>
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={8}>
        {isLoading ? <Skeleton height="450px" borderRadius="2xl" /> : <SearchPopularityChart data={insights?.searches} />}
        {isLoading ? <Skeleton height="450px" borderRadius="2xl" /> : <BookingFunnelChart data={insights?.attempts} />}
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8}>
        <Box gridColumn={{ base: "span 1", lg: "span 2" }}>
           {isLoading ? <Skeleton height="600px" borderRadius="2xl" /> : <FailureAnalytics data={insights?.attempts} />}
        </Box>
        
        <VStack spacing={8}>
          <Card variant="glass" shadow="sm" w="full">
            <CardHeader>
              <Heading size="sm">Insight Summary</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>TOP DEMAND ROUTE</Text>
                  <Text fontWeight="bold" fontSize="lg">{insights?.searches?.[0]?.routeName || 'N/A'}</Text>
                  <Text fontSize="xs" color="cyan.600">{insights?.searches?.[0]?.searchCount || 0} searches this period</Text>
                </Box>
                <Divider />
                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>CRITICAL FRICTION POINT</Text>
                  <Text fontWeight="bold" fontSize="lg" color="red.600" noOfLines={2}>
                    {insights?.attempts?.failureReasons?.[0]?.reason || 'None detected'}
                  </Text>
                  <Text fontSize="xs" color="red.400">Impacting {insights?.attempts?.failureReasons?.[0]?.count || 0} users</Text>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          <Card variant="glass" shadow="sm" w="full" bgGradient="linear(to-br, brand.500, brand.700)" color="white">
            <CardBody>
              <VStack align="start" spacing={3}>
                <Box p={2} bg="whiteAlpha.200" borderRadius="lg">
                  <MousePointer2 size={24} />
                </Box>
                <Text fontWeight="bold" fontSize="xl">Action Items</Text>
                <Text fontSize="sm" opacity={0.9}>
                  Users are mostly searching for <b>{insights?.searches?.[0]?.routeName}</b>. 
                  Consider adding more trips or daladalas to this route to capture demand.
                </Text>
                <Button size="sm" variant="outline" color="white" _hover={{ bg: "whiteAlpha.200" }}>
                  Optimize Schedules
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </SimpleGrid>
    </VStack>
  )
}

function Divider() {
  return <Box h="1px" bg="gray.100" my={2} />
}
